import React, { useState, useEffect, useCallback } from "react";
import T from '../../components/common/T';
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axiosConfig";
import OdooFormLayout from "../../components/OdooFormLayout"; 
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BanknotesIcon, PrinterIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";

// 🟢 IMPORT GLOBAL CURRENCY HOOK
import { useCurrency } from '../../hooks/useCurrency';

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // 🟢 PULL FORMATTER
  const { formatMoney } = useCurrency();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await api.get(`/accounting/invoices/${id}`);
      setInvoice(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  if (loading) return <div className="p-10 text-center text-gray-500"><T>Loading Invoice...</T></div>;
  if (!invoice) return <div className="p-10 text-center text-red-500"><T>Invoice not found</T></div>;

  const ActionButtons = (
    <>
      <button 
        onClick={() => toast.info("Printing feature coming soon!")}
        className="bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 px-3 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-slate-800 text-xs font-bold uppercase flex items-center gap-2 transition"
      >
        <PrinterIcon className="w-4 h-4" /> Print
      </button>

      {invoice.status === 'Draft' && (
          <button 
            onClick={() => toast.success("Marked as Sent (Demo)")}
            className="bg-blue-600 text-white px-4 py-1.5 rounded text-xs font-bold uppercase shadow-sm hover:bg-blue-700 flex items-center gap-2 ml-2 transition"
          >
            <PaperAirplaneIcon className="w-4 h-4" /> Send
          </button>
      )}

      {invoice.status !== 'Paid' && (
        <button 
          onClick={() => toast.info("Payment Modal will open here")}
          className="bg-green-600 text-white px-4 py-1.5 rounded text-xs font-bold uppercase shadow-sm hover:bg-green-700 flex items-center gap-2 ml-2 transition"
        >
          <BanknotesIcon className="w-4 h-4" /> Register Payment
        </button>
      )}
      
      <button 
        onClick={() => navigate(-1)} 
        className="bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 px-3 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-slate-800 text-xs font-bold uppercase ml-2 transition"
      >
        Back
      </button>
    </>
  );

  return (
    <>
      
      <OdooFormLayout
        title={`INV / ${invoice.invoice_number}`}
        subTitle={`Client: ${invoice.client_name}`}
        status={invoice.status}
        stages={["Draft", "Sent", "Paid"]}
        actions={ActionButtons}
      >
        {invoice.status === "Paid" && (
            <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden z-20 pointer-events-none">
                <div className="absolute top-3 -right-8 w-32 bg-green-500 text-white text-xs font-bold py-1.5 text-center transform rotate-45 shadow-md">
                    PAID
                </div>
            </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-8">
            <div className="space-y-4">
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Client</T></label> 
                    <span className="border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 text-gray-900 dark:text-white font-bold">{invoice.client_name}</span>
                </div>
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Project</T></label> 
                    <span className="border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 text-blue-600 dark:text-blue-400 font-medium">
                        {invoice.project_name || "General"}
                    </span>
                </div>
            </div>

            <div className="space-y-4">
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Invoice Date</T></label> 
                    <span className="border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 font-medium text-gray-800 dark:text-slate-200">{new Date(invoice.invoice_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center">
                    <label className="w-32 text-xs font-bold text-gray-700 dark:text-slate-400 uppercase"><T>Due Date</T></label> 
                    <span className={`border-b border-gray-200 dark:border-[#30363d] flex-1 py-1 font-bold ${new Date(invoice.due_date) < new Date() && invoice.status !== 'Paid' ? 'text-red-600 dark:text-rose-400' : 'text-gray-900 dark:text-white'}`}>
                        {new Date(invoice.due_date).toLocaleDateString()}
                    </span>
                </div>
            </div>
        </div>

        <div className="mt-8 border rounded-lg overflow-hidden border-gray-200 dark:border-[#30363d]">
            <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-[#21262d] text-gray-500 dark:text-slate-400 font-bold border-b border-gray-200 dark:border-[#30363d]">
                    <tr>
                        <th className="px-4 py-3"><T>Description</T></th>
                        <th className="px-4 py-3 text-right"><T>Quantity</T></th>
                        <th className="px-4 py-3 text-right"><T>Unit Price</T></th>
                        <th className="px-4 py-3 text-right"><T>Tax (18%)</T></th>
                        <th className="px-4 py-3 text-right"><T>Amount</T></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
                    {invoice.items && invoice.items.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                            <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">{item.description}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300">{item.quantity}</td>
                            {/* 🟢 REPLACED <Price> with formatMoney */}
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300">{formatMoney(Number(item.unit_price))}</td>
                            <td className="px-4 py-3 text-right text-gray-500 dark:text-slate-400">{formatMoney(Number(item.amount * 0.18))}</td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">{formatMoney(Number(item.amount))}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        <div className="flex justify-end mt-6">
            <div className="w-72 bg-gray-50 dark:bg-[#21262d] p-4 rounded-lg border border-gray-100 dark:border-[#30363d]">
                <div className="flex justify-between text-sm text-gray-600 dark:text-slate-400 mb-2">
                    <span><T>Subtotal:</T></span>
                    <span>{formatMoney(Number(invoice.sub_total))}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-slate-400 mb-2 border-b border-gray-200 dark:border-[#30363d] pb-2">
                    <span><T>Tax Total:</T></span>
                    <span>{formatMoney(Number(invoice.tax_total))}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-white pt-2">
                    <span><T>Grand Total:</T></span>
                    <span>{formatMoney(Number(invoice.grand_total))}</span>
                </div>
                <div className="flex justify-between text-sm text-green-600 dark:text-emerald-400 mt-2 pt-2 border-t border-gray-200 dark:border-[#30363d]">
                    <span><T>Amount Paid:</T></span>
                    <span>- {formatMoney(Number(invoice.paid_amount || 0))}</span>
                </div>
                <div className="flex justify-between text-lg font-black text-red-600 dark:text-rose-400 mt-1">
                    <span><T>Balance Due:</T></span>
                    <span>{formatMoney(Number(invoice.balance_due))}</span>
                </div>
            </div>
        </div>

      </OdooFormLayout>
    </>
  );
}